import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useCallback, useState } from 'react';
import { loginRequest } from '../../../const/msal-config';
import { basePath } from '../../../utils/base-path';
import { useLinkExternalUserMutation } from './mutations/useLinkExternalUserMutation';

export const useMicrosoftAuth = () => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: loginExternalUser } = useLinkExternalUserMutation();

  // Starts the redirect flow: navigates away to Microsoft. The actual login result is
  // handled on return by `handleRedirectResult`. Using a redirect (instead of a popup)
  // is required inside the APK Custom Tab, where the popup's `window.opener` link is lost.
  const login = useCallback(async () => {
    const otherActionInProgress = inProgress !== InteractionStatus.None || isLoading;
    if (otherActionInProgress) {
      return;
    }

    await instance.loginRedirect({ ...loginRequest, prompt: 'select_account' });
  }, [inProgress, instance, isLoading]);

  // Completes the redirect flow at startup: reads the result left by `loginRedirect` and
  // links it to a Parse session. Returns the linked Parse user, or undefined when the app
  // was not opened as a result of a Microsoft redirect. Loading is only raised once a result
  // is actually present so regular app loads don't flash a loader.
  const handleRedirectResult = useCallback(async () => {
    const result = await instance.handleRedirectPromise();
    if (!result) {
      return undefined;
    }

    setIsLoading(true);
    try {
      return await loginExternalUser({
        id: result.account.localAccountId,
        accessToken: result.accessToken,
        username: result.account.username,
      });
    } finally {
      setIsLoading(false);
    }
  }, [instance, loginExternalUser]);

  const logout = useCallback(
    async (username?: string) => {
      if (isAuthenticated) {
        const currentAccount = username ? instance.getAccountByUsername(username) : undefined;
        await instance.logoutRedirect({ account: currentAccount, postLogoutRedirectUri: basePath });
      }
    },
    [instance, isAuthenticated]
  );

  return {
    login,
    logout,
    handleRedirectResult,
    isAuthenticated,
    isLoading,
  };
};
